import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { isEnDefaultMarketLocale } from '#src/app/pages/petitionDetails/shared/utilities';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { isMobile } from '#src/app/shared/utils/userAgent';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['petitionSignFormCtaCopyExperiment'];

type Params = {
	experiment: Experiment;
	utilityContext: UtilityContext;
};

/**
 * Unlike its sibling normalizers in this directory (which return only `Experiment['variation']`),
 * this one returns the full, `treated`-corrected experiment object. `SignFormCtaCopyLabel` passes it
 * straight into `useTrackExperimentExposureOnMount`, which needs `treated`/`fallback` to decide
 * whether to fire `$exposure` — narrowing this to a bare variation string would silently break that.
 */
export async function normalizePetitionSignFormCtaCopyExperiment({
	experiment,
	utilityContext,
}: Params): Promise<Experiment> {
	if (!shouldRunExperiment({ experiment, utilityContext })) {
		// Experiment Service bucketing is keyed on UUID alone, so `experiment.variation`/`treated` can
		// already reflect a treatment arm here even though this user shouldn't see one (non-mobile,
		// non-en-default-market, or FC disabled) — e.g. a user treated on a prior mobile/en-US visit
		// revisiting on desktop. Force `control` so the rendered copy respects the gate, and force
		// `treated: false` so `useTrackExperimentExposure` doesn't fire a control `$exposure` for this
		// out-of-scope visit.
		return { ...experiment, variation: 'control', treated: false };
	}

	if (experiment.override) {
		// A QA/dev `?exo.` override already forced `variation`/`override: true` upstream, and
		// `useTrackExperimentExposure` already no-ops `$exposure` for `override`. Pass it through
		// unchanged instead of calling `treatExperiment`: that hits the real Experiment Service treat
		// endpoint for the session UUID, which would register a real bucket assignment for a visit
		// that was never a genuine assignment — polluting `experiment_variations` for that user.
		return experiment;
	}

	await treatExperiment(utilityContext, experiment.experimentName);

	return { ...experiment, treated: true };
}

function shouldRunExperiment({ experiment, utilityContext }: Params): boolean {
	// CHANGE-95457 targets en-default markets (US, GB, CA, AU, NZ, IE) via an Optimizely audience
	// filter on geoip.country_code — but that filtering can go stale the same way the mobile check
	// above does, so we re-verify locale here too. SignFormCtaCopyLabel also only has variant copy for
	// these locales (see TRANSLATIONS there) and falls back to the production copy otherwise — gating
	// treatment here keeps `treated` (and therefore `$exposure`) in sync with what the user actually saw.
	return (
		isEnabledByFC(experiment) && isMobile(utilityContext) && isEnDefaultMarketLocale(utilityContext.i18n.getLocale())
	);
}

/**
 * This experiment uses a static `.withExperimentName(...)`, unlike the dynamic-name pattern (e.g.
 * `supporterGrowthEmbedSignPetitionFormExperiment`) that signals "FC disabled" via an
 * `'__invalid_experiment__'` experimentName sentinel. Here, `enabledIf` failing (and any other
 * resolution fallback — fetch errors, the experiment not being registered yet, etc.) surfaces instead
 * as `fallback: true` on the resolved experiment (see `createExperimentsProvider.ts`'s
 * `getVariationForExperimentService` in the middle-end) — that's the signal to check instead.
 */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
